import { useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { UserRow, UserRole } from '@/lib/types';

// Module-level guard so the last-seen UPDATE only fires once per bundle load
// per user. useSession() is consumed by N components (Sidebar, AuthGuard,
// per-route hooks), each with its own useEffect / without this they'd all
// fire UPDATEs in parallel on mount.
let lastSeenTouchedFor: string | null = null;
async function touchLastSeenOnce(userId: string): Promise<void> {
  if (lastSeenTouchedFor === userId) return;
  lastSeenTouchedFor = userId;
  try {
    await supabase
      .from('users')
      .update({ last_seen_at: new Date().toISOString() })
      .eq('id', userId);
  } catch (e) {
    // Best-effort: failures here are silent. Used by analytics "recently
    // active" view / missing one bump doesn't break anything user-facing.
    console.warn('[last_seen] failed to touch', e);
    lastSeenTouchedFor = null; // allow retry on next mount
  }
}

// Composite hook: returns the live Supabase auth Session AND the linked
// public.users row (with role). Centralizes the "am I logged in + what can
// I do" question for every component.
//
// Behavior:
//   - On mount: getSession() once + onAuthStateChange subscription
//   - Profile is fetched via TanStack Query (cached, refetched on signin)
//   - Returns ready=true only when BOTH session resolved and profile loaded
//     (or known-absent if logged out)

export interface AuthState {
  session: Session | null;
  profile: UserRow | null;
  role: UserRole | null;
  isStaff: boolean;
  isAdmin: boolean;
  isAuthenticated: boolean;
  ready: boolean;
}

export function useSession(): AuthState {
  const [session, setSession] = useState<Session | null>(null);
  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setSessionReady(true);
      if (data.session?.user?.id) touchLastSeenOnce(data.session.user.id);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setSessionReady(true);
      if (s?.user?.id) touchLastSeenOnce(s.user.id);
    });
    return () => { mounted = false; sub.subscription.unsubscribe(); };
  }, []);

  const userId = session?.user?.id ?? null;

  const profileQuery = useQuery<UserRow | null>({
    queryKey: ['profile', userId],
    enabled: !!userId,
    staleTime: 60_000,
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const profile = profileQuery.data ?? null;
  const role    = profile?.role ?? null;

  // ready when:
  //  - session resolved AND
  //  - if logged in, profile fetched (success or error counted as "settled")
  const ready =
    sessionReady &&
    (!session || profileQuery.isFetched);

  return {
    session,
    profile,
    role,
    isStaff:        role === 'admin' || role === 'editor' || role === 'reviewer',
    isAdmin:        role === 'admin',
    isAuthenticated: !!session,
    ready,
  };
}
